import {
  WSAlertProps,
  WSDataItemProps,
  WSGrid,
  WSList
} from "@wingspanhq/fe-component-library";
import { IPayerTaxFormResponse } from "@wingspanhq/payments/dist/interfaces";
import { CompanyStructure } from "@wingspanhq/users/dist/lib/interfaces";
import { useMemo } from "react";
import { DataItem } from "../../../../../shared/components/DataItem";
import { selectorAddressString } from "../../../../../shared/selectors/selectorAddressString";
import { selectorTaxClassificationLabel } from "../../../../../shared/selectors/selectorTaxClassificationLabel";
import { getTaxIdVerificationAlert } from "../../../../../shared/utils/taxId";
import { selectorPayerTaxFormDefaultStatus } from "../../../selectors/selectorPayerTaxFormDefaultStatus";
import { selectorPayerTaxFormDefaultTinType } from "../../../selectors/selectorPayerTaxFormDefaultTinType";
import { selectorTaxFormPayeeCountry } from "../../../selectors/selectorTaxFormPayeeCountry";
import { selectorTaxFormRecipientName } from "../../../selectors/selectorTaxFormRecipientName";
import { selectorTINVerificationStatusDataView } from "../../../selectors/selectorTINVerificationStatusDataView";
import { useQueryFeatureFlags } from "@hooks/useQueryFeatureFlags";
import { TaxFormValidationAlert } from "./TaxFormValidationAlert";

type Props = {
  taxForm: IPayerTaxFormResponse;
  onEdit?: () => void;
};

export const ContractorDetails: React.FC<Props> = ({ taxForm, onEdit }) => {
  const featureFlags = useQueryFeatureFlags();

  const fields = useMemo(() => {
    const submissionData = taxForm.data;
    const payeeW9Data = submissionData?.w9Info;
    const tinStatus = selectorPayerTaxFormDefaultStatus(taxForm);
    const payeeCountry = selectorTaxFormPayeeCountry(taxForm);
    const tinType = selectorPayerTaxFormDefaultTinType(taxForm);

    const companyStructure = payeeW9Data?.companyStructure;
    const isSinglePerson =
      companyStructure === CompanyStructure.LlcSingleMember ||
      companyStructure === CompanyStructure.SoleProprietorship;
    const isBusinessRequiringLegalName =
      tinType === "Business" && !isSinglePerson;
    const isLegalBusinessNameMissing =
      isBusinessRequiringLegalName && !payeeW9Data?.legalBusinessName;

    let nameLabel: string;
    if (isSinglePerson && payeeW9Data?.legalBusinessName) {
      nameLabel = "Legal business name / name";
    } else if (isBusinessRequiringLegalName) {
      nameLabel = "Legal business name";
    } else {
      nameLabel = "Legal name";
    }

    const nameValue = isLegalBusinessNameMissing
      ? "--"
      : selectorTaxFormRecipientName(taxForm);

    const nameAlert: WSAlertProps | undefined = isLegalBusinessNameMissing
      ? {
          theme: "warning",
          icon: "alert",
          title: "Required for contractors operating with EIN"
        }
      : undefined;

    const result: WSDataItemProps[] = [
      {
        label: nameLabel,
        value: nameValue,
        alert: nameAlert
      },
      {
        label: "Federal tax classification",
        value:
          tinType === "Individual"
            ? "Sole proprietorship / Single member LLC"
            : payeeW9Data?.companyStructure
              ? selectorTaxClassificationLabel(payeeW9Data?.companyStructure)
              : undefined
      },
      {
        label: "Taxpayer identification number",
        value: selectorTINVerificationStatusDataView(tinType, payeeW9Data),
        alert: getTaxIdVerificationAlert({
          country: payeeCountry,
          status: tinStatus,
          isPendingPaused: featureFlags.data?.is1099FilingPaused
        })
      },
      {
        label: "Address",
        value: selectorAddressString(payeeW9Data)
      }
    ];

    return result;
  }, [taxForm]);

  return (
    <WSList gap="L">
      <WSGrid gutter={{ xs: "L", s: "XL", m: "2XL" }}>
        {fields.map((props, index) => (
          <WSGrid.Item
            key={`${props.label} + ${index}`}
            span={{ xs: "12", s: "6" }}
          >
            <DataItem {...props} />
          </WSGrid.Item>
        ))}
      </WSGrid>
      <TaxFormValidationAlert taxForm={taxForm} onEdit={onEdit} />
    </WSList>
  );
};
